// ==========================================
// CADASTRO MOTORISTA — BoraFrete SEM SENHA
// ✅ Login Google puro — sem campo senha
// ✅ Status: 'em_analise' (aguarda aprovação do admin)
// ✅ CORREÇÃO: INSERT direto (não update fallback) pra criar registro novo
// ✅ CORREÇÃO: resetSubmit() em TODAS as validações
// ==========================================
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, garantirSessao } from '@/lib/supabase';
import { useAuth } from '@/lib/AuthContext';
import { 
  Truck, User, Mail, Phone, MapPin, FileText, 
  CreditCard, ArrowRight, CheckCircle, AlertCircle, 
  Loader2, Search
} from 'lucide-react';
import { toast } from 'sonner';

// ==========================================
// FUNÇÕES UTILITÁRIAS
// ==========================================
const limparNumeros = (valor) => valor ? valor.replace(/\D/g, '') : '';

const formatarCPF = (cpf) => {
  const numeros = limparNumeros(cpf);
  if (numeros.length <= 3) return numeros;
  if (numeros.length <= 6) return `${numeros.slice(0, 3)}.${numeros.slice(3)}`;
  if (numeros.length <= 9) return `${numeros.slice(0, 3)}.${numeros.slice(3, 6)}.${numeros.slice(6)}`;
  return `${numeros.slice(0, 3)}.${numeros.slice(3, 6)}.${numeros.slice(6, 9)}-${numeros.slice(9, 11)}`;
};

const formatarTelefone = (tel) => {
  const numeros = limparNumeros(tel);
  if (numeros.length <= 2) return numeros;
  if (numeros.length <= 7) return `(${numeros.slice(0, 2)}) ${numeros.slice(2)}`;
  return `(${numeros.slice(0, 2)}) ${numeros.slice(2, 7)}-${numeros.slice(7, 11)}`;
};

const formatarPlaca = (placa) => {
  const valor = placa.toUpperCase().replace(/[^A-Z0-9]/g, '');
  const limpo = valor.slice(0, 7);
  if (limpo.length <= 3) return limpo;
  return `${limpo.slice(0, 3)}-${limpo.slice(3)}`;
};

const validarCPF = (cpf) => {
  const cpfLimpo = limparNumeros(cpf);
  if (cpfLimpo.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(cpfLimpo)) return false;
  
  let soma = 0;
  let resto;
  
  for (let i = 1; i <= 9; i++) {
    soma += parseInt(cpfLimpo.substring(i - 1, i)) * (11 - i);
  }
  
  resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) resto = 0;
  if (resto !== parseInt(cpfLimpo.substring(9, 10))) return false;
  
  soma = 0;
  for (let i = 1; i <= 10; i++) {
    soma += parseInt(cpfLimpo.substring(i - 1, i)) * (12 - i);
  }
  
  resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) resto = 0;
  if (resto !== parseInt(cpfLimpo.substring(10, 11))) return false;
  
  return true;
};

// ==========================================
// COMPONENTE
// ==========================================
export default function CadastroMotorista() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [buscandoCep, setBuscandoCep] = useState(false);
  const [cpfValido, setCpfValido] = useState(null);
  const [userId, setUserId] = useState(null);
  const [authEmail, setAuthEmail] = useState('');
  const [motoristaExiste, setMotoristaExiste] = useState(false);
  const [sessaoVerificada, setSessaoVerificada] = useState(false);
  
  const isSubmittingRef = useRef(false);
  const cepTimerRef = useRef(null);

  const [formData, setFormData] = useState({
    nome: '',
    cpf: '',
    telefone: '',
    cnh: '',
    categoria_cnh: 'C',
    cep: '',
    endereco: '',
    numero: '',
    bairro: '',
    cidade: '',
    estado: '',
    placa_caminhao: ''
  });

  // ==========================================
  // VERIFICA USUÁRIO LOGADO
  // ==========================================
  useEffect(() => {
    const checkUser = async () => {
      console.log('[CadastroMotorista] Verificando sessão...');
      
      const { user, session, error } = await garantirSessao();

      if (!user) {
        console.error('❌ [CadastroMotorista] Sem sessão:', error);
        toast.error('Sessão expirada. Faça login novamente.');
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
        return;
      }

      console.log('✅ [CadastroMotorista] Sessão ativa:', user.id);
      setUserId(user.id);
      setAuthEmail(user.email);
      setSessaoVerificada(true);

      // Busca motorista existente
      try {
        const { data: motorista } = await supabase
          .from('motoristas')
          .select('*')
          .eq('usuario_id', user.id)
          .maybeSingle();

        if (motorista) {
          setMotoristaExiste(true);
          setFormData(prev => ({
            ...prev,
            nome: motorista.nome_completo || '',
            cpf: motorista.cpf ? formatarCPF(motorista.cpf) : '',
            telefone: motorista.telefone ? formatarTelefone(motorista.telefone) : '',
            cnh: motorista.cnh || '',
            categoria_cnh: motorista.categoria_cnh || 'C',
            cep: motorista.cep || '',
            endereco: motorista.endereco || '',
            numero: motorista.numero || '',
            bairro: motorista.bairro || '',
            cidade: motorista.cidade || '',
            estado: motorista.estado || '',
            placa_caminhao: motorista.placa_caminhao || ''
          }));
          if (motorista.cpf) setCpfValido(true);
        }
      } catch (err) {
        console.error('Erro ao buscar motorista:', err);
      }
    };

    checkUser();
  }, []);

  useEffect(() => {
    return () => {
      if (cepTimerRef.current) clearTimeout(cepTimerRef.current);
    };
  }, []);

  // ==========================================
  // HANDLERS
  // ==========================================
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCpfChange = (e) => {
    const numeros = limparNumeros(e.target.value);
    if (numeros.length > 11) return;
    
    setFormData(prev => ({ ...prev, cpf: formatarCPF(numeros) }));
    
    if (numeros.length === 11) {
      setCpfValido(validarCPF(numeros));
    } else {
      setCpfValido(null);
    }
  };

  const handleTelefoneChange = (e) => {
    setFormData(prev => ({ ...prev, telefone: formatarTelefone(e.target.value) }));
  };

  const handleCnhChange = (e) => {
    const valor = limparNumeros(e.target.value).slice(0, 11);
    setFormData(prev => ({ ...prev, cnh: valor }));
  };

  const handleCepChange = (e) => {
    const numeros = limparNumeros(e.target.value).slice(0, 8);
    const formatado = numeros.replace(/(\d{5})(\d)/, '$1-$2');
    setFormData(prev => ({ ...prev, cep: formatado }));

    if (cepTimerRef.current) clearTimeout(cepTimerRef.current);
    
    if (numeros.length === 8) {
      setBuscandoCep(true);
      cepTimerRef.current = setTimeout(async () => {
        try {
          const response = await fetch(`https://viacep.com.br/ws/${numeros}/json/`);
          const data = await response.json();
          
          if (!data.erro) {
            setFormData(prev => ({
              ...prev,
              endereco: data.logradouro || '',
              bairro: data.bairro || '',
              cidade: data.localidade || '',
              estado: data.uf || ''
            }));
            toast.success('Endereço preenchido automaticamente!');
          }
        } catch (err) {
          console.log('Erro CEP:', err);
        } finally {
          setBuscandoCep(false);
        }
      }, 500);
    }
  };

  const handlePlacaChange = (e) => {
    setFormData(prev => ({ ...prev, placa_caminhao: formatarPlaca(e.target.value) }));
  };

  // ==========================================
  // ✅ CORREÇÃO: Função que reseta o estado do botão
  // ==========================================
  const resetSubmit = () => {
    isSubmittingRef.current = false;
    setLoading(false);
  };

  // ==========================================
  // SUBMIT — SEM SENHA + INSERT CORRIGIDO
  // ==========================================
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setLoading(true);

    try {
      const { user: currentUser, error: sessaoError } = await garantirSessao();

      if (!currentUser) {
        toast.error('Erro de autenticação. Faça login novamente.');
        window.location.href = '/login';
        return;
      }

      // --- Validações com resetSubmit() em TODOS os returns ---
      if (!formData.nome.trim() || formData.nome.trim().length < 3) {
        toast.error('Nome completo é obrigatório (mínimo 3 caracteres)');
        resetSubmit();
        return;
      }
      
      if (!formData.cpf || !validarCPF(formData.cpf)) {
        toast.error('CPF inválido');
        resetSubmit();
        return;
      }
      
      const telLimpo = limparNumeros(formData.telefone);
      if (!telLimpo || telLimpo.length < 10) {
        toast.error('Telefone/WhatsApp inválido');
        resetSubmit();
        return;
      }
      
      if (!formData.cnh || formData.cnh.length < 9) {
        toast.error('CNH inválida (mínimo 9 dígitos)');
        resetSubmit();
        return;
      }
      
      const placaLimpo = formData.placa_caminhao.replace(/[^A-Z0-9]/g, '');
      if (!formData.placa_caminhao || placaLimpo.length < 7) {
        toast.error('Placa do caminhão inválida. Use o formato ABC-1234 ou ABC-1D23');
        resetSubmit();
        return;
      }
      
      if (!formData.cidade || !formData.estado) {
        toast.error('Cidade e estado são obrigatórios');
        resetSubmit();
        return;
      }

      // --- Verifica duplicidade CPF ---
      const cpfLimpo = limparNumeros(formData.cpf);
      const { data: cpfExiste } = await supabase
        .from('motoristas')
        .select('id')
        .eq('cpf', cpfLimpo)
        .neq('usuario_id', currentUser.id)
        .maybeSingle();

      if (cpfExiste) {
        toast.error('Este CPF já está cadastrado em outra conta.');
        resetSubmit();
        return;
      }

      // --- Salva motorista ---
      const dadosMotorista = {
        usuario_id: currentUser.id,
        nome_completo: formData.nome.trim(),
        cpf: cpfLimpo,
        telefone: telLimpo,
        cnh: formData.cnh,
        categoria_cnh: formData.categoria_cnh,
        placa_caminhao: formData.placa_caminhao.toUpperCase(),
        cidade: formData.cidade,
        estado: formData.estado,
        cep: limparNumeros(formData.cep) || null,
        endereco: formData.endereco.trim() || null,
        numero: formData.numero.trim() || null,
        bairro: formData.bairro.trim() || null,
        email: currentUser.email,
        status: 'em_analise',
        updated_at: new Date().toISOString()
      };

      console.log('📤 [CadastroMotorista] Salvando motorista:', dadosMotorista);

      // ✅ CORREÇÃO CRÍTICA: INSERT direto (não upsert) pra criar registro novo
      const { data: insertData, error: insertError } = await supabase
        .from('motoristas')
        .insert(dadosMotorista)
        .select();

      if (insertError) {
        console.error('❌ Erro no insert:', insertError);
        
        // Se der erro de duplicado (usuario_id já existe), tenta UPDATE
        if (insertError.code === '23505') {
          console.log('🔄 Conflito detectado, tentando UPDATE...');
          const { data: upData, error: upErr } = await supabase
            .from('motoristas')
            .update(dadosMotorista)
            .eq('usuario_id', currentUser.id)
            .select();
            
          if (upErr) {
            console.error('❌ Erro no update:', upErr);
            throw new Error('Erro ao atualizar: ' + upErr.message);
          }
          console.log('✅ Atualizado com sucesso:', upData);
        } else {
          throw new Error('Erro ao salvar: ' + insertError.message);
        }
      } else {
        console.log('✅ Inserido com sucesso:', insertData);
      }

      toast.success('Cadastro enviado! Aguarde aprovação do admin. 🎉');
      
      await refreshUser();
      navigate('/painel');

    } catch (error) {
      console.error('❌ Erro:', error);
      toast.error(error.message || 'Erro ao salvar');
    } finally {
      resetSubmit();
    }
  };

  // ==========================================
  // RENDER
  
  // Loading inicial
  if (!sessaoVerificada && !authEmail) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-orange-400 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Verificando autenticação...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-orange-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
            <Truck className="w-8 h-8 text-orange-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">
            {motoristaExiste ? 'Completar Cadastro' : 'Cadastro de Motorista'}
          </h1>
          <p className="text-slate-400">Complete seus dados para começar a transportar</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8 space-y-6">
          
          {/* Email (readonly - do auth) */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="email"
                value={authEmail}
                readOnly
                className="w-full pl-10 pr-4 py-3 bg-slate-700/30 border border-slate-600 rounded-lg text-slate-400 cursor-not-allowed"
              />
            </div>
            <p className="text-slate-500 text-xs mt-1">Email vinculado à conta Google</p>
          </div>

          {/* NOME */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Nome Completo *</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                name="nome"
                value={formData.nome}
                onChange={handleChange}
                required
                className="w-full pl-10 pr-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-orange-500 outline-none"
                placeholder="Ex: João Silva"
              />
            </div>
          </div>

          {/* CPF com validação visual */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">CPF *</label>
            <div className="relative">
              <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                name="cpf"
                value={formData.cpf}
                onChange={handleCpfChange}
                required
                maxLength={14}
                className={`w-full pl-10 pr-12 py-3 bg-slate-700/50 border rounded-lg text-white placeholder-slate-400 focus:ring-2 outline-none ${
                  cpfValido === true ? 'border-green-500 focus:ring-green-500' : 
                  cpfValido === false ? 'border-red-500 focus:ring-red-500' : 
                  'border-slate-600 focus:ring-orange-500'
                }`}
                placeholder="000.000.000-00"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {cpfValido === true && <CheckCircle className="w-5 h-5 text-green-400" />}
                {cpfValido === false && <AlertCircle className="w-5 h-5 text-red-400" />}
              </div>
            </div>
            {cpfValido === false && (
              <p className="text-red-400 text-sm mt-1">CPF inválido! Verifique os dígitos.</p>
            )}
          </div>

          {/* TELEFONE */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">WhatsApp / Telefone *</label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="tel"
                name="telefone"
                value={formData.telefone}
                onChange={handleTelefoneChange}
                required
                maxLength={15}
                className="w-full pl-10 pr-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-orange-500 outline-none"
                placeholder="(00) 00000-0000"
              />
            </div>
          </div>

          {/* CNH E CATEGORIA */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">CNH *</label>
              <div className="relative">
                <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  name="cnh"
                  value={formData.cnh}
                  onChange={handleCnhChange}
                  required
                  maxLength={11}
                  className="w-full pl-10 pr-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-orange-500 outline-none"
                  placeholder="Número da CNH"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Categoria *</label>
              <select
                name="categoria_cnh"
                value={formData.categoria_cnh}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-orange-500 outline-none"
              >
                <option value="C">C - Caminhão</option>
                <option value="D">D - Ônibus</option>
                <option value="E">E - Carreta</option>
                <option value="CE">CE - Combinada</option>
              </select>
            </div>
          </div>

          {/* PLACA DO CAMINHÃO */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Placa do Caminhão *</label>
            <div className="relative">
              <Truck className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                name="placa_caminhao"
                value={formData.placa_caminhao}
                onChange={handlePlacaChange}
                required
                maxLength={8}
                className="w-full pl-10 pr-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-orange-500 outline-none uppercase"
                placeholder="ABC-1234"
              />
            </div>
            <p className="text-slate-500 text-xs mt-1">Formato: ABC-1234 ou ABC-1D23</p>
          </div>

          {/* CEP com busca automática */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">CEP</label>
            <div className="relative">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${buscandoCep ? 'text-blue-400 animate-pulse' : 'text-slate-400'}`} />
              <input
                type="text"
                name="cep"
                value={formData.cep}
                onChange={handleCepChange}
                maxLength={9}
                className="w-full pl-10 pr-12 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-orange-500 outline-none"
                placeholder="00000-000"
              />
              {buscandoCep && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Loader2 className="w-5 h-5 text-blue-400 animate-spin" />
                </div>
              )}
            </div>
          </div>

          {/* ENDEREÇO */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Endereço</label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                name="endereco"
                value={formData.endereco}
                onChange={handleChange}
                className="w-full pl-10 pr-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-orange-500 outline-none"
                placeholder="Rua, Avenida, etc."
              />
            </div>
          </div>

          {/* NÚMERO E BAIRRO */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Número</label>
              <input
                type="text"
                name="numero"
                value={formData.numero}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-orange-500 outline-none"
                placeholder="123"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Bairro</label>
              <input
                type="text"
                name="bairro"
                value={formData.bairro}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-orange-500 outline-none"
                placeholder="Centro"
              />
            </div>
          </div>

          {/* CIDADE E ESTADO */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Cidade *</label>
              <input
                type="text"
                name="cidade"
                value={formData.cidade}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-orange-500 outline-none"
                placeholder="Cuiabá"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Estado *</label>
              <select
                name="estado"
                value={formData.estado}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-orange-500 outline-none"
              >
                <option value="">Selecione</option>
                <option value="AC">Acre</option>
                <option value="AL">Alagoas</option>
                <option value="AP">Amapá</option>
                <option value="AM">Amazonas</option>
                <option value="BA">Bahia</option>
                <option value="CE">Ceará</option>
                <option value="DF">Distrito Federal</option>
                <option value="ES">Espírito Santo</option>
                <option value="GO">Goiás</option>
                <option value="MA">Maranhão</option>
                <option value="MT">Mato Grosso</option>
                <option value="MS">Mato Grosso do Sul</option>
                <option value="MG">Minas Gerais</option>
                <option value="PA">Pará</option>
                <option value="PB">Paraíba</option>
                <option value="PR">Paraná</option>
                <option value="PE">Pernambuco</option>
                <option value="PI">Piauí</option>
                <option value="RJ">Rio de Janeiro</option>
                <option value="RN">Rio Grande do Norte</option>
                <option value="RS">Rio Grande do Sul</option>
                <option value="RO">Rondônia</option>
                <option value="RR">Roraima</option>
                <option value="SC">Santa Catarina</option>
                <option value="SP">São Paulo</option>
                <option value="SE">Sergipe</option>
                <option value="TO">Tocantins</option>
              </select>
            </div>
          </div>

          {/* BOTÃO SUBMIT */}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 bg-orange-600 text-white font-bold rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all"
          >
            {loading ? (
              <><Loader2 className="w-5 h-5 animate-spin" /> Salvando...</>
            ) : (
              <><CheckCircle className="w-5 h-5" /> {motoristaExiste ? 'Atualizar Cadastro' : 'Finalizar Cadastro'}</>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}